import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type {
  FabCompanyRef,
  FabCountry,
  FabFilters,
  FabScholarshipParams,
  FabScholarshipRow,
  FabSchoolParams,
  FabSchoolRow,
  FabSummary,
} from '@/lib/types';
import {
  FAB_STAGES,
  toScholarshipParams,
  toSchoolParams,
  type FabFilterState,
  type FabStage,
} from './fab-filter-state';

/**
 * The SEED FAB page's shared data layer. Every tab reads the same fetch set,
 * so switching tabs never refetches.
 *
 * - ONE fetch set per filter state: the scholarships summary plus the schools
 *   summary for each of the three stages (with `sourceSplit` and
 *   `productSplit` on), in parallel.
 * - The fetch key is the wire params, NOT the filter state: `search` (`q`) is
 *   a client-side filter on the Schools and Scholarships tabs, and `stage` is
 *   only the Schools tab's view toggle (all three stages are always fetched),
 *   so neither refetches.
 * - Previous data stays on screen while a new key loads (`refreshing`).
 *   `loading` is only true when there is nothing to show yet.
 * - Loading is DERIVED from request keys (a result is current only while its
 *   key matches), never set inside the effect.
 */

export interface FabStageRow {
  schoolId: string;
  displayName: string;
  domain: string | null;
  country: string | null;
  countryCode: string | null;
  company: FabCompanyRef | null;
  applications: number;
  admits: number;
  enrolled: number;
  byStage: Record<FabStage, FabSchoolRow | null>;
}

export interface FabOverviewData {
  /** With the current filters (search excluded). */
  scholarships: FabSummary<FabScholarshipRow>;
  /** Three stages, sourceSplit + productSplit on. */
  schools: Record<FabStage, FabSummary<FabSchoolRow>>;
  /** Merged by schoolId, applications desc. */
  merged: FabStageRow[];
  /** The OLDEST `fetchedAt` of the four reads: the age of the stalest figure. */
  fetchedAt: string;
}

/* --- formatting ----------------------------------------------------------- */

const countFormat = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });

/** A fraction (0–1+); null when the denominator is 0. */
export function stageRate(numerator: number, denominator: number): number | null {
  if (denominator === 0) return null;
  return numerator / denominator;
}

/** Grouped integer; 0 renders "0"; null/undefined renders "—". */
export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return countFormat.format(n);
}

/** `stageRate` output as a whole percentage ("34%"); null renders "—". */
export function formatRate(r: number | null): string {
  if (r === null || !Number.isFinite(r)) return '—';
  return `${Math.round(r * 100)}%`;
}

/* --- request params ------------------------------------------------------- */

interface FabRequest {
  scholarships: FabScholarshipParams;
  /** Stage-less; the hook adds each stage and the two splits. */
  schools: Omit<FabSchoolParams, 'stage'>;
}

function toRequest(state: FabFilterState): FabRequest {
  const { search: _schoolSearch, stage: _stage, ...schools } = toSchoolParams(state);
  const { search: _scholarshipSearch, ...scholarships } = toScholarshipParams(state);
  return { scholarships, schools };
}

function oldest(isos: string[]): string {
  return isos.reduce((min, iso) => (Date.parse(iso) < Date.parse(min) ? iso : min));
}

/** Union of the three stages' rows, keyed by schoolId; identity from the earliest stage that has it. */
export function mergeStages(schools: Record<FabStage, FabSummary<FabSchoolRow>>): FabStageRow[] {
  const byId = new Map<string, FabStageRow>();
  for (const stage of FAB_STAGES) {
    for (const row of schools[stage].rows) {
      let merged = byId.get(row.schoolId);
      if (merged === undefined) {
        merged = {
          schoolId: row.schoolId,
          displayName: row.displayName,
          domain: row.domain,
          country: row.country,
          countryCode: row.countryCode,
          company: row.company,
          applications: 0,
          admits: 0,
          enrolled: 0,
          byStage: { applications: null, admits: null, enrolled: null },
        };
        byId.set(row.schoolId, merged);
      }
      merged.byStage[stage] = row;
      merged[stage] = row.total;
      if (merged.company === null && row.company !== null) merged.company = row.company;
    }
  }
  return [...byId.values()].sort(
    (a, b) =>
      b.applications - a.applications ||
      b.admits - a.admits ||
      b.enrolled - a.enrolled ||
      a.displayName.localeCompare(b.displayName),
  );
}

/* --- useFabOverview ------------------------------------------------------- */

interface OverviewResult {
  key: string;
  data: FabOverviewData | null;
  error: unknown | null;
}

/**
 * `data` is the latest SUCCESSFUL fetch set, kept while a newer key loads or
 * after a newer key fails (so a failed refresh leaves the last figures in
 * place); `error` is set only for the current key.
 */
export function useFabOverview(
  state: FabFilterState,
  reloadToken: number,
): { data: FabOverviewData | null; loading: boolean; error: unknown | null; refreshing: boolean } {
  const paramsKey = JSON.stringify(toRequest(state));
  const requestKey = `${paramsKey}#${reloadToken}`;
  const [lastData, setLastData] = useState<{ key: string; data: FabOverviewData } | null>(null);
  const [result, setResult] = useState<OverviewResult | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const key = `${paramsKey}#${reloadToken}`;
    const request = JSON.parse(paramsKey) as FabRequest;
    const schoolsFor = (stage: FabStage) =>
      api.getFabSchools(
        { ...request.schools, stage, sourceSplit: true, productSplit: true },
        signal,
      );

    Promise.all([
      api.getFabScholarships(request.scholarships, signal),
      schoolsFor('applications'),
      schoolsFor('admits'),
      schoolsFor('enrolled'),
    ])
      .then(([scholarships, applications, admits, enrolled]) => {
        if (signal.aborted) return;
        const schools: Record<FabStage, FabSummary<FabSchoolRow>> = {
          applications,
          admits,
          enrolled,
        };
        const data: FabOverviewData = {
          scholarships,
          schools,
          merged: mergeStages(schools),
          fetchedAt: oldest([
            scholarships.fetchedAt,
            applications.fetchedAt,
            admits.fetchedAt,
            enrolled.fetchedAt,
          ]),
        };
        setLastData({ key, data });
        setResult({ key, data, error: null });
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setResult({ key, data: null, error: err });
      });

    return () => controller.abort();
  }, [paramsKey, reloadToken]);

  const current = result !== null && result.key === requestKey ? result : null;
  const error = current?.error ?? null;
  const data = lastData?.data ?? null;
  const settled = current !== null;
  return {
    data,
    loading: data === null && !settled,
    error,
    refreshing: data !== null && !settled,
  };
}

/* --- useFabFilters -------------------------------------------------------- */

const NO_COUNTRIES: FabCountry[] = [];

interface FiltersResult {
  key: string;
  filters: FabFilters | null;
  error: unknown | null;
}

interface CountriesResult {
  key: string;
  countries: FabCountry[] | null;
  error: unknown | null;
}

/**
 * The filter catalogue for one SEED FAB cycle (null = SEED FAB's current)
 * plus the country list (cycle-independent). The previous cycle's catalogue
 * stays visible while a new cycle's loads.
 */
export function useFabFilters(cycleId: number | null): {
  filters: FabFilters | null;
  countries: FabCountry[];
  loading: boolean;
  error: unknown | null;
  reload(): void;
} {
  const [reloadToken, setReloadToken] = useState(0);
  const [filtersResult, setFiltersResult] = useState<FiltersResult | null>(null);
  const [lastFilters, setLastFilters] = useState<FabFilters | null>(null);
  const [countriesResult, setCountriesResult] = useState<CountriesResult | null>(null);
  // The last GOOD list of each, so a failed reload leaves the pickers populated.
  const [lastCountries, setLastCountries] = useState<FabCountry[] | null>(null);

  const filtersKey = `${cycleId ?? 'current'}#${reloadToken}`;
  const countriesKey = String(reloadToken);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const key = `${cycleId ?? 'current'}#${reloadToken}`;
    api
      .getFabFilters(cycleId ?? undefined, signal)
      .then((filters) => {
        if (signal.aborted) return;
        setLastFilters(filters);
        setFiltersResult({ key, filters, error: null });
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setFiltersResult({ key, filters: null, error: err });
      });
    return () => controller.abort();
  }, [cycleId, reloadToken]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const key = String(reloadToken);
    api
      .listFabCountries(signal)
      .then((countries) => {
        if (signal.aborted) return;
        setLastCountries(countries);
        setCountriesResult({ key, countries, error: null });
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setCountriesResult({ key, countries: null, error: err });
      });
    return () => controller.abort();
  }, [reloadToken]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  const currentFilters = filtersResult?.key === filtersKey ? filtersResult : null;
  const currentCountries = countriesResult?.key === countriesKey ? countriesResult : null;

  return {
    filters: lastFilters,
    countries: lastCountries ?? NO_COUNTRIES,
    loading: currentFilters === null || currentCountries === null,
    error: currentFilters?.error ?? currentCountries?.error ?? null,
    reload,
  };
}
